import styled from "@emotion/styled";
import { LinearProgress, Tooltip } from "@mui/material";
import type { ReactNode } from "react";

import { useFormatMessage } from "@skilitics/localization";

import { IconButton } from "../../../buttons/Buttons";
import { DEFAULT_INPUT_HEIGHT, FONT_WEIGHT_SEMI_BOLD } from "../../../utilities";
import type { TestAttribute } from "../../shared";

type Mode = "empty" | "dragging" | "processing" | "selected" | "error";

const ControlContainer = styled.div`
  display: grid;
  height: 100%;
  width: 100%;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  user-select: none;
`;

const FileContainer = styled.div<{ mode: Mode }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${DEFAULT_INPUT_HEIGHT}px;
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  transition: all 200ms ease-in-out;
  cursor: pointer;

  ${({ mode, theme }) => {
    switch (mode) {
      case "empty":
        return `
          border: 1px dashed ${theme.outline};
          background: ${theme.surfaceVariant};
        `;
      case "dragging":
        return `
          border: 1px solid ${theme.primary};
          background: ${theme.primaryContainer};
          color: ${theme.onPrimaryContainer};
        `;
      case "processing":
        return `
          display: block;
          border: 1px solid ${theme.outline};
          background-color: transparent;
        `;
      case "selected":
        return `
          justify-content: space-between;
        `;
      case "error":
        return `

          background-color: ${theme.errorContainer};
          color: ${theme.onErrorContainer};
        `;
    }
  }}
`;

const Progress = styled(LinearProgress)`
  margin-bottom: 4px;
  border-radius: 4px 4px 0 0;
`;

const Content = styled.div`
  width: 100%;
  text-align: center;
`;

const ProvidedContent = styled.div`
  width: 100%;
`;

type FileDropzoneComponentProps = TestAttribute & {
  children: ReactNode;
  errorMessage?: string;
  hidden?: boolean;
  mode: Mode;
  selectedOverride?: ReactNode;
  onCancel: () => void;
  onDismiss: () => void;
  onRemove: () => void;
  typeLabel?: string;
};

export const FileDropzoneComponent = ({
  children,
  dataTest,
  errorMessage,
  hidden,
  mode,
  onCancel,
  onDismiss,
  onRemove,
  typeLabel,
}: FileDropzoneComponentProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const handleClick = () => {
    if (mode === "processing") {
      onCancel();
    }
  };

  let content;
  switch (mode) {
    case "empty":
    case "dragging":
    case "processing":
      content = <Content data-test="content-container">{formatMessage(mode, { typeLabel })}</Content>;
      break;
    case "error":
      content = (
        <>
          <Tooltip title={errorMessage} data-test="error-tooltip">
            <Content>{formatMessage("error")}</Content>
          </Tooltip>
          <IconButton onClick={onDismiss} data-test="dismiss-button" icon="times" />
        </>
      );
      break;
    case "selected":
      content = (
        <>
          <ProvidedContent>{children}</ProvidedContent>

          <IconButton onClick={onRemove} icon="minus" data-test="remove-button" />
        </>
      );
      break;
    default:
      console.warn("FileDropzoneComponent: mode not implemented", mode);
      break;
  }

  return (
    <ControlContainer data-test={dataTest}>
      <FileContainer
        className="file-container"
        mode={mode}
        onClick={handleClick}
        data-test={`${dataTest ?? "file-drop-zone"}-file-container`}
        hidden={hidden}
      >
        {mode === "processing" && <Progress />}
        {content}
      </FileContainer>
    </ControlContainer>
  );
};

const formatStrings = {
  empty: {
    en: "Drop {typeLabel} or click to upload",
  },
  dragging: {
    en: "Drop {typeLabel} to upload",
  },
  processing: {
    en: "Uploading...",
  },
  error: {
    en: "An unexpected error occurred.",
  },
};
